import { useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import type { SeoMeta } from "@/types"

export default function DemoThankYou({
  meta,
  name,
}: {
  meta: SeoMeta
  name?: string
}) {
  const firstName = name?.split(" ")[0]

  useEffect(() => {
    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead", { content_name: "demo_request" })
    }
  }, [])

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content="noindex" />
      </Head>

      <section className="px-6 py-24 md:py-36">
        <div className="mx-auto max-w-2xl text-center">
          {/* Success icon */}
          <div className="mx-auto flex size-20 items-center justify-center rounded-full bg-red/10">
            <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-10">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
          </div>

          <h1 className="mt-8 font-display text-4xl font-bold tracking-tight text-charcoal md:text-5xl">
            {firstName ? (
              <>
                Demo booked, {firstName}<span className="text-red">.</span>
              </>
            ) : (
              <>
                Demo request received<span className="text-red">.</span>
              </>
            )}
          </h1>

          <p className="mx-auto mt-6 max-w-lg text-lg text-slate">
            We&apos;ll be in touch within one working day to arrange a time that
            works for you. The call takes about 20 minutes.
          </p>

          {/* What to expect */}
          <div className="mx-auto mt-12 max-w-md text-left">
            <h2 className="font-display text-lg font-bold text-charcoal">
              What to expect<span className="text-red">.</span>
            </h2>
            <ol className="mt-5 space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-red/10 text-xs font-bold text-red">
                  1
                </span>
                <div>
                  <p className="text-sm font-medium text-charcoal">We arrange a call</p>
                  <p className="mt-0.5 text-xs text-slate">
                    A member of our team will email you to find a convenient time
                    for your demo.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-red/10 text-xs font-bold text-red">
                  2
                </span>
                <div>
                  <p className="text-sm font-medium text-charcoal">We demo with your listings</p>
                  <p className="mt-0.5 text-xs text-slate">
                    Send us a listing URL beforehand and we&apos;ll create a real
                    video from it during the call - so you see exactly what your
                    properties will look like.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-red/10 text-xs font-bold text-red">
                  3
                </span>
                <div>
                  <p className="text-sm font-medium text-charcoal">You keep the video</p>
                  <p className="mt-0.5 text-xs text-slate">
                    The video we create during the demo is yours to keep - post it,
                    share it, use it however you like. No strings attached.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          {/* Links */}
          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="/"
              className="inline-flex items-center rounded-md border border-stone bg-white px-6 py-3 text-sm font-medium text-charcoal transition-colors hover:bg-stone"
            >
              Back to home
            </Link>
            <Link
              href="/examples"
              className="inline-flex items-center rounded-md bg-cream px-6 py-3 text-sm font-medium text-charcoal transition-colors hover:bg-stone"
            >
              See example videos
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
